import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Spin } from "antd";
import { toast } from "react-toastify";
import {
  getInstructionsForJob,
  getPrintInstruction,
} from "../../api/workInstructionsApi";
import "./WorkInstructionPrint.css";

/**
 * Print-friendly view for work instructions.
 *
 * Routes:
 *  - /job/:jobId/instructions/print   → prints ALL instructions for a job
 *  - /work-instruction/:instructionId/print → prints a SINGLE instruction
 */
const WorkInstructionPrint = () => {
  const { jobId, instructionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [jobId, instructionId]);

  const fetchData = async () => {
    try {
      if (instructionId) {
        // Single instruction print view (uses /print endpoint for formatted data)
        const result = await getPrintInstruction(instructionId);
        setData([result]);
      } else if (jobId) {
        // All instructions for a job
        const result = await getInstructionsForJob(jobId);
        setData(Array.isArray(result) ? result : result?.data || []);
      }
      // Auto-print after content loads
      setTimeout(() => window.print(), 600);
    } catch (error) {
      toast.error("Failed to load instructions for printing");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" tip="Loading print view..." />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        No instructions to print.
      </div>
    );
  }

  return (
    <div className="work-instruction-print">
      {data.map((instruction, idx) => (
        <div key={instruction.id || idx} className="instruction-page">
          {/* ── Header ────────────────────────────────────── */}
          <div className="print-header">
            <div className="company-name">Pack ERP</div>
            <h1>WORK INSTRUCTION</h1>
            <div className="instruction-meta">
              <span>
                Job #: <strong>{instruction.job_number}</strong>
              </span>
              <span>
                Stage: <strong>{instruction.stage_name}</strong>
              </span>
              <span>
                Type: <strong>{instruction.instruction_type}</strong>
              </span>
              <span>Date: {new Date().toLocaleDateString()}</span>
            </div>
            {instruction.qr_code && (
              <div className="qr-info">QR: {instruction.qr_code}</div>
            )}
          </div>

          {/* ── Job Info ──────────────────────────────────── */}
          {instruction.content?.header && (
            <table className="info-table">
              <tbody>
                <tr>
                  <td><strong>Item:</strong></td>
                  <td>{instruction.content.header.item_name}</td>
                  <td><strong>Quantity:</strong></td>
                  <td>{instruction.content.header.quantity?.toLocaleString()}</td>
                </tr>
                <tr>
                  <td><strong>Customer:</strong></td>
                  <td>{instruction.content.header.customer_id}</td>
                  <td><strong>Job #:</strong></td>
                  <td>{instruction.content.header.job_number}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* ── Expected Output ───────────────────────────── */}
          {instruction.content?.output && (
            <div className="section">
              <h2>Expected Output</h2>
              <table className="data-table">
                <tbody>
                  {Object.entries(instruction.content.output).map(([key, value]) => (
                    <tr key={key}>
                      <td className="label">{key.replace(/_/g, " ")}</td>
                      <td>{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Paper Reels ───────────────────────────────── */}
          {["paper_reel_1", "paper_reel_2"].map((reelKey) => {
            const reel = instruction.content?.[reelKey];
            if (!reel) return null;
            return (
              <div className="section" key={reelKey}>
                <h2>{reel.label || reelKey.replace(/_/g, " ").toUpperCase()}</h2>
                <table className="data-table">
                  <tbody>
                    {Object.entries(reel)
                      .filter(([k]) => k !== "label")
                      .map(([key, value]) => (
                        <tr key={key}>
                          <td className="label">{key.replace(/_/g, " ")}</td>
                          <td>{String(value)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* ── Gum / Adhesive ────────────────────────────── */}
          {instruction.content?.gum && (
            <div className="section">
              <h2>Adhesive / Gum</h2>
              <table className="data-table">
                <tbody>
                  <tr>
                    <td className="label">Type</td>
                    <td>{instruction.content.gum.type}</td>
                  </tr>
                  <tr>
                    <td className="label">Estimated Quantity</td>
                    <td>{instruction.content.gum.estimated_kg} kg</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* ── Machine Settings ──────────────────────────── */}
          {instruction.content?.machine_settings && (
            <div className="section">
              <h2>Machine Settings</h2>
              <div className="parameters-grid">
                {Object.entries(instruction.content.machine_settings).map(
                  ([key, value]) => (
                    <div key={key} className="parameter-box">
                      <div className="param-label">
                        {key.replace(/_/g, " ").toUpperCase()}
                      </div>
                      <div className="param-value">{String(value)}</div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* ── Generic Machine Setup (fallback) ──────────── */}
          {instruction.content?.machine_setup && (
            <div className="section">
              <h2>Machine Setup</h2>
              <table className="data-table">
                <tbody>
                  {Object.entries(instruction.content.machine_setup).map(
                    ([key, value]) => (
                      <tr key={key}>
                        <td className="label">{key.replace(/_/g, " ")}</td>
                        <td>
                          {typeof value === "object"
                            ? JSON.stringify(value)
                            : String(value)}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Generic Parameters (fallback) ─────────────── */}
          {instruction.content?.parameters && (
            <div className="section">
              <h2>Process Parameters</h2>
              <div className="parameters-grid">
                {Object.entries(instruction.content.parameters).map(
                  ([key, value]) => (
                    <div key={key} className="parameter-box">
                      <div className="param-label">
                        {key.replace(/_/g, " ").toUpperCase()}
                      </div>
                      <div className="param-value">{String(value)}</div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* ── Materials ─────────────────────────────────── */}
          {((instruction.materials && instruction.materials.length > 0) ||
            (instruction.content?.materials &&
              instruction.content.materials.length > 0)) && (
            <div className="section">
              <h2>Materials Required</h2>
              <table className="data-table striped">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Material</th>
                    <th>Quantity</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {(instruction.materials || instruction.content.materials).map(
                    (mat, i) => (
                      <tr key={i}>
                        <td>{mat.category || "-"}</td>
                        <td>{mat.name}</td>
                        <td>{mat.quantity}</td>
                        <td>{mat.unit}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Process Steps ─────────────────────────────── */}
          {instruction.content?.steps?.length > 0 && (
            <div className="section">
              <h2>Process Steps</h2>
              <ol className="steps-list">
                {instruction.content.steps.map((step, i) => (
                  <li key={i}>
                    <strong>{step.title || `Step ${i + 1}`}</strong>
                    <p>{step.description}</p>
                    {step.warning && (
                      <div className="warning-box">⚠️ {step.warning}</div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* ── Quality Checks ────────────────────────────── */}
          {instruction.content?.quality_checks?.length > 0 && (
            <div className="section">
              <h2>Quality Checkpoints</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Checkpoint</th>
                    <th>Specification</th>
                    <th>✓</th>
                  </tr>
                </thead>
                <tbody>
                  {instruction.content.quality_checks.map((check, i) => (
                    <tr key={i}>
                      <td>{check.checkpoint}</td>
                      <td>{check.specification}</td>
                      <td className="checkbox-cell">☐</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Safety Notes ──────────────────────────────── */}
          {instruction.content?.safety_notes?.length > 0 && (
            <div className="section safety-section">
              <h2>⚠️ Safety Notes</h2>
              <ul>
                {instruction.content.safety_notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Operator Sign-Off ─────────────────────────── */}
          <div className="section">
            <h2>Operator Sign-Off</h2>
            {instruction.content?.operator_fields && (
              <table className="data-table">
                <tbody>
                  {Object.entries(instruction.content.operator_fields).map(
                    ([key, value]) => (
                      <tr key={key}>
                        <td className="label">{key.replace(/_/g, " ")}</td>
                        <td>{value || "_________________"}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Footer ────────────────────────────────────── */}
          <div className="print-footer">
            <div className="signature-line">
              <span>Operator Signature: _________________</span>
              <span>Supervisor: _________________</span>
              <span>Date: _________________</span>
            </div>
            <div className="page-number">
              Page {idx + 1} of {data.length}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WorkInstructionPrint;
